import { AccountSettings, aktiviereOrganisation, useAuth } from '@xhubio-saas/core-frontend/kernel'
import { LogAuditPage } from '@xhubio-saas/core-frontend/plugin-log-audit'
import {
  localStorageAdapter,
  OnboardingYamlPage,
} from '@xhubio-saas/core-frontend/plugin-onboarding-yaml'
import { RoleDetailPage, RoleListPage } from '@xhubio-saas/core-frontend/plugin-roles'
import { SubscriptionPage } from '@xhubio-saas/core-frontend/plugin-subscription'
import { UsagePage } from '@xhubio-saas/core-frontend/plugin-usage'
import {
  type AcceptInvitationCallbacks,
  AcceptInvitationPage,
  type AcceptOwnershipTransferCallbacks,
  AcceptOwnershipTransferPage,
  UserDetailPage,
  UserListPage,
  VerifyEmailChangePage,
} from '@xhubio-saas/core-frontend/plugin-user'
import { ApiKeysPage } from '@xhubio-saas/frontend-plugin-api-key'
import { type Template, TemplateListPage } from '@xhubio-saas/pdf-frontend/plugin-pdf'
import { useCallback, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useApiKeyClient } from './api/apiKeyClient'
import { toListTemplate, usePdfTemplateClient } from './api/pdfTemplateClient'
import { useRoleClient } from './api/roleClient'
import { useSubscriptionClient } from './api/subscriptionClient'
import { useUsageClient } from './api/usageClient'
import { useUserClient } from './api/userClient'
import { ConsentGuard } from './components/ConsentGuard'
import { useCanInviteMembers, useCanManageMemberStatus } from './hooks/useMemberAdminRights'
import { SettingsLayout } from './layouts/SettingsLayout'
import { ChangelogPage } from './pages/protected/ChangelogPage'
import {
  CookieSettingsPage,
  LegalSettingsPage,
  SettingsSystemPage,
} from './pages/protected/settings'

// ═══════════════════════════════════════════════════════════════════════════
// Page Wrapper Components
// ═══════════════════════════════════════════════════════════════════════════

import { onboardingConfigs } from './onboarding/configs'

/**
 * Onboarding YAML Page with locale-aware config selection.
 *
 * Picks the DE/EN YAML config based on i18n.language. UI button labels
 * and stepper-counter text are translated automatically via the
 * `onboardingYaml` i18next namespace (registered by core-frontend), so
 * no explicit `labels` prop is needed.
 */
export const ONBOARDING_STORAGE = localStorageAdapter('onboarding-yaml:pdf-api-standard')

export function OnboardingYamlPageWithProviders() {
  const { i18n } = useTranslation()
  const locale: 'de' | 'en' = i18n.language?.startsWith('de') ? 'de' : 'en'
  const config = onboardingConfigs[locale]
  return (
    <OnboardingYamlPage
      config={config}
      storage={ONBOARDING_STORAGE}
      stepperShowMore={false}
      stepperVariant="compact"
      onComplete={async (answers) => {
        window.dispatchEvent(new CustomEvent('onboarding-yaml:complete', { detail: { answers } }))
      }}
    />
  )
}

/**
 * API Keys Page with real backend connection
 */
export function ApiKeysPageWithProviders() {
  const { t, i18n } = useTranslation('apiKey')
  const apiClient = useApiKeyClient()
  // Vertrag des Pakets: zweiter Parameter = Interpolation (`{ count }`, `{ name }`),
  // nicht Fallback (dist/pages/ApiKeysPage.d.ts). Den Fallback setzt das Paket
  // selbst, wenn `t(key)` den Schlüssel zurückgibt. Anders TemplateListPage unten:
  // dort ist der zweite Parameter wirklich der Fallback.
  const translate = useCallback(
    (key: string, params?: Record<string, unknown>) => t(key, params),
    [t]
  )
  return (
    <ConsentGuard>
      <ApiKeysPage apiClient={apiClient} t={translate} locale={i18n.language} />
    </ConsentGuard>
  )
}

// ═══════════════════════════════════════════════════════════════════════════
// Users / Team (plugin-user)
// ═══════════════════════════════════════════════════════════════════════════

export function UserListPageWithProviders() {
  const { t } = useTranslation('user')
  const navigate = useNavigate()
  const apiClient = useUserClient()
  // 🔴 Beide Props fuehren in core-frontend den Default `false`. Wer sie nicht
  // setzt, nimmt Einladen und Aktivieren/Deaktivieren ALLEN weg, auch dem
  // Eigentuemer — sichtbar wird das erst im Browser, nie im Build.
  const canInvite = useCanInviteMembers()
  const canManageStatus = useCanManageMemberStatus()
  return (
    <ConsentGuard>
      <UserListPage
        apiClient={apiClient}
        t={t}
        canInvite={canInvite}
        canManageStatus={canManageStatus}
        onViewDetail={(member) => navigate(`/users/${member.id}`)}
        // 🔴 core-frontend 4.0.0: die Detailseite IST die Formularseite —
        // eine separate `/users/:memberId/edit`-Route gibt es nicht mehr.
        // `onEdit` bleibt eine Pflicht-Prop von `UserListPage`, zeigt aber auf
        // dasselbe Ziel wie `onViewDetail`.
        onEdit={(member) => navigate(`/users/${member.id}`)}
      />
    </ConsentGuard>
  )
}

export function UserDetailPageWithProviders() {
  const { t } = useTranslation('user')
  const { memberId } = useParams<{ memberId: string }>()
  const navigate = useNavigate()
  // 🔴 Die Route-Id MUSS in den Client. Seit core-frontend 4.0.0 IST die
  // Detailseite die Formularseite und speichert selbst; ohne Ziel-Id bindet
  // `useUserClient()` an „ich selbst", und der Server loest die betroffene
  // Person allein aus der Sitzung auf. Ein Admin, der ein Mitglied pflegt,
  // schreibt dessen Werte dann still auf sein EIGENES Profil — die Maske zeigt
  // die richtige Person, der Toast meldet Erfolg. Hausprazedenz: verein
  // `d2c2768`, dashandwerk `da0f1eb`, invoice-api `516d403`.
  const apiClient = useUserClient(memberId)
  // Siehe oben — Default `false`, also muss der Wert gesetzt werden.
  const canManageStatus = useCanManageMemberStatus()
  return (
    <ConsentGuard>
      <UserDetailPage
        apiClient={apiClient}
        memberId={memberId ?? ''}
        t={t}
        canManageStatus={canManageStatus}
        onBack={() => navigate('/users')}
      />
    </ConsentGuard>
  )
}

export function AcceptInvitationPageWithProviders() {
  const { t } = useTranslation('user')
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const { user, session } = useAuth()
  const apiClient = useUserClient()

  const token = searchParams.get('token') ?? ''

  const callbacks: AcceptInvitationCallbacks = {
    isLoggedIn: () => !!session,
    getCurrentUserEmail: () => user?.email ?? null,
    navigateToRegister: (tkn: string, email: string) => {
      navigate(`/register?invitation=${tkn}&email=${encodeURIComponent(email)}`)
    },
    navigateToLogin: (redirectUrl: string) => {
      navigate(`/login?redirect=${encodeURIComponent(redirectUrl)}`)
    },
    setActiveOrganization: async (organizationId: string) => {
      await aktiviereOrganisation(organizationId)
      await new Promise((resolve) => setTimeout(resolve, 100))
    },
    navigateToDashboard: () => {
      window.location.href = '/usage'
    },
  }

  return <AcceptInvitationPage apiClient={apiClient} callbacks={callbacks} token={token} t={t} />
}

/**
 * Verify Email Change Page (public)
 */
export function VerifyEmailChangePageWithProviders() {
  const { t } = useTranslation('user')
  const [searchParams] = useSearchParams()
  const apiClient = useUserClient()
  return (
    <VerifyEmailChangePage
      apiClient={apiClient}
      searchParams={{
        token: searchParams.get('token') ?? undefined,
        action: searchParams.get('action') ?? undefined,
      }}
      onBackToLogin={() => {
        window.location.href = '/login'
      }}
      t={t}
    />
  )
}

/**
 * Accept Ownership Transfer Page (public)
 */
export function AcceptOwnershipTransferPageWithProviders() {
  const { t } = useTranslation('user')
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const { session } = useAuth()
  const apiClient = useUserClient()

  const callbacks: AcceptOwnershipTransferCallbacks = {
    isLoggedIn: () => !!session,
    navigateToLogin: (redirectUrl: string) => {
      navigate(`/login?redirect=${encodeURIComponent(redirectUrl)}`)
    },
    navigateToDashboard: () => {
      window.location.href = '/usage'
    },
    setActiveOrganization: async (organizationId: string) => {
      await aktiviereOrganisation(organizationId)
      await new Promise((resolve) => setTimeout(resolve, 100))
    },
  }

  return (
    <AcceptOwnershipTransferPage
      apiClient={apiClient}
      callbacks={callbacks}
      searchParams={{
        token: searchParams.get('token') ?? undefined,
        action: searchParams.get('action') ?? undefined,
      }}
      t={t}
    />
  )
}

// ═══════════════════════════════════════════════════════════════════════════
// Roles (plugin-roles)
// ═══════════════════════════════════════════════════════════════════════════

export function RoleListPageWithProviders() {
  const { t } = useTranslation('roles')
  const navigate = useNavigate()
  const apiClient = useRoleClient()
  return (
    <ConsentGuard>
      <RoleListPage
        apiClient={apiClient}
        t={t}
        onNavigateToDetail={(roleId) => navigate(`/settings/roles/${roleId}`)}
      />
    </ConsentGuard>
  )
}

export function RoleDetailPageWithProviders() {
  const { t } = useTranslation('roles')
  const { roleId } = useParams<{ roleId: string }>()
  const navigate = useNavigate()
  const apiClient = useRoleClient()
  return (
    <ConsentGuard>
      <RoleDetailPage
        apiClient={apiClient}
        roleId={roleId ?? ''}
        t={t}
        onBack={() => navigate('/settings/roles')}
      />
    </ConsentGuard>
  )
}

/**
 * Subscription Page with real backend connection
 */
export function SubscriptionPageWithProviders() {
  const { t, i18n } = useTranslation('subscription')
  const subscriptionClient = useSubscriptionClient()

  // Map i18n language code to locale
  const localeMap: Record<string, string> = {
    de: 'de-DE',
    en: 'en-US',
    fr: 'fr-FR',
    es: 'es-ES',
    it: 'it-IT',
    nl: 'nl-NL',
    pl: 'pl-PL',
    pt: 'pt-PT',
    cs: 'cs-CZ',
    hu: 'hu-HU',
    ro: 'ro-RO',
    bg: 'bg-BG',
  }
  const locale = localeMap[i18n.language] || 'en-US'

  // Build translations object for SubscriptionPage
  const translations = {
    page: {
      title: t('page.title', 'Subscription'),
      description: t('page.description', 'Manage your subscription and billing'),
      loading: t('page.loading', 'Loading subscription...'),
      error: t('page.error', 'Failed to load subscription data'),
    },
    plan: {
      title: t('plan.title', 'Current Plan'),
      period: t('plan.period', 'Billing Period'),
      trialEnds: t('plan.trialEnds', 'Trial ends on {{date}}'),
      creditsUsed: t('plan.creditsUsed', 'Credits used'),
      creditsRemaining: t('plan.creditsRemaining', '{{count}} credits remaining'),
      unlimited: t('plan.unlimited', 'Unlimited credits'),
      pastDueWarning: t(
        'plan.pastDueWarning',
        'Your payment is past due. Please update your payment method.'
      ),
      updatePayment: t('plan.updatePayment', 'Update payment'),
      statusLabels: {
        active: t('status.active', 'Active'),
        trialing: t('status.trialing', 'Trial'),
        canceled: t('status.canceled', 'Canceled'),
        past_due: t('status.past_due', 'Past Due'),
        paused: t('status.paused', 'Paused'),
        incomplete: t('status.incomplete', 'Incomplete'),
        incomplete_expired: t('status.incomplete_expired', 'Expired'),
      },
    },
    billing: {
      monthly: t('billing.monthly', 'Monthly'),
      yearly: t('billing.yearly', 'Yearly'),
      yearlyDiscount: t('billing.yearlyDiscount', '-20%'),
    },
    promo: {
      earlyStageOffer: t(
        'promo.earlyStageOffer',
        'Early Stage Deal: {{discount}} off for the first 3 months'
      ),
      discount: t('promo.discount', '-50% (3 Mon.)'),
      yearlySavings: t('promo.yearlySavings', '€{{amount}} saved per year'),
    },
    pricing: {
      free: t('pricing.free', 'Free'),
      perMonth: t('pricing.perMonth', '/month'),
      creditsIncluded: t('pricing.creditsIncluded', 'API Calls included'),
      extraCredits: t('pricing.extraCredits', '+€{{price}}/API Call after'),
      andMore: t('pricing.andMore', '+{{count}} more'),
    },
    badges: {
      current: t('badges.current', 'Current'),
      popular: t('badges.popular', 'Popular'),
    },
    button: {
      currentPlan: t('button.currentPlan', 'Current Plan'),
      manageSubscription: t('button.manageSubscription', 'Manage Subscription'),
    },
    invoice: {
      title: t('invoice.title', 'Invoices'),
      description: t('invoice.description', 'Your billing history'),
      date: t('invoice.date', 'Date'),
      number: t('invoice.number', 'Invoice Number'),
      amount: t('invoice.amount', 'Amount'),
      status: t('invoice.status', 'Status'),
      actions: t('invoice.actions', 'Actions'),
      view: t('invoice.view', 'View'),
      download: t('invoice.download', 'Download'),
      empty: t('invoice.empty', 'No invoices yet'),
      statusLabels: {
        draft: t('invoice.statusLabels.draft', 'Draft'),
        open: t('invoice.statusLabels.open', 'Open'),
        paid: t('invoice.statusLabels.paid', 'Paid'),
        void: t('invoice.statusLabels.void', 'Void'),
        uncollectible: t('invoice.statusLabels.uncollectible', 'Uncollectible'),
      },
    },
    noPlans: {
      title: t('noPlans.title', 'No plans available'),
      description: t(
        'noPlans.description',
        'There are no subscription plans configured yet. You are on the free plan with unlimited access.'
      ),
      freePlanDescription: t('noPlans.freePlanDescription', 'Unlimited access — no restrictions.'),
    },
  }

  return (
    <ConsentGuard>
      <SubscriptionPage client={subscriptionClient} translations={translations} locale={locale} />
    </ConsentGuard>
  )
}

/**
 * Usage Overview Page with providers
 */
export function UsagePageWithProviders() {
  const { t, i18n } = useTranslation('usage')
  const usageClient = useUsageClient()

  // Map i18n language code to locale
  const localeMap: Record<string, string> = {
    de: 'de-DE',
    en: 'en-US',
    fr: 'fr-FR',
    es: 'es-ES',
    it: 'it-IT',
    nl: 'nl-NL',
    pl: 'pl-PL',
    pt: 'pt-PT',
    cs: 'cs-CZ',
    hu: 'hu-HU',
    ro: 'ro-RO',
    bg: 'bg-BG',
  }
  const locale = localeMap[i18n.language] || 'en-US'

  // Get eventTypes directly from resource bundle (keys contain : and . which i18next interprets as separators)
  const resourceBundle = i18n.getResourceBundle(i18n.language, 'usage') as
    | { eventTypes?: Record<string, string> }
    | undefined
  const eventTypes: Record<string, string> = resourceBundle?.eventTypes ?? {}

  // Build translations object for UsagePage
  const translations = {
    pageTitle: t('page.title'),
    pageDescription: t('page.description'),
    quotaOverview: t('page.quotaOverview'),
    usageHistory: t('page.usageHistory'),
    refresh: t('page.refresh'),
    lastUpdated: t('page.lastUpdated'),
    loading: t('page.loading'),
    error: t('page.error'),
    retry: t('page.retry'),
    plan: {
      title: t('plan.title'),
      period: t('plan.period'),
      trialEnds: t('plan.trialEnds'),
      statusLabels: {
        active: t('status.active'),
        trialing: t('status.trialing'),
        canceled: t('status.canceled'),
        past_due: t('status.past_due'),
        paused: t('status.paused'),
        incomplete: t('status.incomplete'),
        incomplete_expired: t('status.incomplete_expired'),
      },
    },
    quota: {
      used: t('quota.used'),
      usedUnlimited: t('quota.usedUnlimited'),
      resetAt: t('quota.resetAt'),
      exceeded: t('quota.exceeded'),
      periodLabel: t('quota.periodLabel'),
    },
    sections: {
      monthly: t('sections.monthly'),
      daily: t('sections.daily'),
      weekly: t('sections.weekly'),
      yearly: t('sections.yearly'),
    },
    periods: {
      monthly: t('periods.monthly'),
      daily: t('periods.daily'),
      weekly: t('periods.weekly'),
      yearly: t('periods.yearly'),
    },
    chart: {
      title: t('chart.title'),
      daily: t('chart.daily'),
      weekly: t('chart.weekly'),
      monthly: t('chart.monthly'),
      legend: t('chart.legend'),
      noData: t('chart.noData'),
      total: t('chart.total'),
    },
    eventTypes,
    footer: {
      lastUpdated: t('footer.lastUpdated'),
      refresh: t('footer.refresh'),
    },
    errors: {
      loadFailed: t('errors.loadFailed'),
      retry: t('errors.retry'),
    },
    alerts: {
      title: t('alerts.title'),
      markAllRead: t('alerts.markAllRead'),
      noAlerts: t('alerts.noAlerts'),
      quotaAt: t('alerts.quotaAt'),
      usedOf: t('alerts.usedOf'),
      rateLimitBlocked: t('alerts.rateLimitBlocked'),
      rateLimitApproaching: t('alerts.rateLimitApproaching'),
      upgradePlan: t('alerts.upgradePlan'),
      timeAgo: {
        justNow: t('alerts.timeAgo.justNow'),
        minutes: t('alerts.timeAgo.minutes'),
        hours: t('alerts.timeAgo.hours'),
        days: t('alerts.timeAgo.days'),
      },
    },
    rateLimit: {
      title: t('rateLimit.title'),
      perMinute: t('rateLimit.perMinute'),
      perHour: t('rateLimit.perHour'),
      resetsIn: t('rateLimit.resetsIn'),
    },
    hierarchy: {
      expand: t('hierarchy.expand'),
      collapse: t('hierarchy.collapse'),
    },
    metrics: {
      pdfGeneration: t('metrics.pdfGeneration'),
      eInvoices: t('metrics.eInvoices'),
      inboundInvoices: t('metrics.inboundInvoices'),
      validations: t('metrics.validations'),
      allEvents: t('metrics.allEvents'),
      thisMonth: t('metrics.thisMonth'),
      total: t('metrics.total'),
    },
    overage: {
      title: t('overage.title', 'Overage'),
      description: t('overage.description', 'Usage beyond your plan limits'),
      included: t('overage.included', 'Included'),
      used: t('overage.used', 'Used'),
      overage: t('overage.overage', 'Overage'),
      estimatedCost: t('overage.estimatedCost', 'Estimated Cost'),
      noOverage: t('overage.noOverage', 'No overage'),
      perUnit: t('overage.perUnit', 'per unit'),
    },
  }

  return (
    <ConsentGuard>
      <UsagePage client={usageClient} translations={translations} locale={locale} />
    </ConsentGuard>
  )
}

/**
 * PDF Template List Page V2 — host for the new block-native editor
 * alongside the legacy one. Same templates table on the backend,
 * differentiated by `editorVersion` inside the BlockTemplate JSONB.
 *
 * Shows both v1 and v2 rows with badges. All rows are clickable here
 * (the new editor opens v1 too — with a warn-modal — so the user can
 * promote a v1 template to v2). The "+ Neu" action creates a v2
 * template directly.
 */
export function PdfTemplateListV2PageWithProviders() {
  const { t } = useTranslation('pdf')
  const location = useLocation()
  const templateClient = usePdfTemplateClient()

  // Free-form type combobox: instead of a fixed "Document" Select we
  // surface every documentType observed in the loaded templates plus
  // a few sensible defaults. In V2 the type has no functional meaning
  // — the user can invent any string ("Report", "Letter",
  // "Certificate", …).
  const [knownTypes, setKnownTypes] = useState<string[]>([])

  // Keep handleLoadTemplates identity-stable. tRPC's useUtils +
  // useMutation return fresh references each render; without this ref
  // the callback identity would change every render, retrigger
  // TemplateListPage's load-effect, and infinite-loop.
  const clientRef = useRef(templateClient)
  clientRef.current = templateClient

  const handleLoadTemplates = useCallback(async (): Promise<
    { success: true; templates: Template[] } | { success: false; error: string }
  > => {
    const result = await clientRef.current.loadProjectList()
    if (result.success) {
      const types = Array.from(
        new Set(result.projects.map((p) => p.documentType).filter((v): v is string => !!v))
      ).sort()
      // Bail out when the set hasn't changed so we don't churn the
      // consumer's state.
      setKnownTypes((prev) =>
        prev.length === types.length && prev.every((v, i) => v === types[i]) ? prev : types
      )
      return { success: true, templates: result.projects.map(toListTemplate) }
    }
    return result
  }, [])

  const handleDeleteTemplate = useCallback(
    async (id: string): Promise<{ success: true } | { success: false; error: string }> => {
      return templateClient.deleteTemplate(id)
    },
    [templateClient]
  )

  const handleDuplicateTemplate = useCallback(
    async (
      id: string
    ): Promise<{ success: true; newId: string } | { success: false; error: string }> => {
      return templateClient.duplicateTemplate(id)
    },
    [templateClient]
  )

  // Anlegen ueber den geteilten Client wie dashandwerk und verein: er saet
  // Papierformat und Einheit aus dem Massystem des Betriebs (`starterTemplate`,
  // imperial ⇒ LETTER/inch) und schickt `documentType` samt JSONB-Spiegel
  // `templateType`. Vorher stand hier eine eigene Mutation mit fester
  // A4/mm-Saat — Plan 16 S3 war so nie erreichbar (Issue PDF-API 10-14).
  const handleCreateTemplate = useCallback(
    async (input: {
      name: string
      description?: string
      type: string
    }): Promise<{ success: true; id: string } | { success: false; error: string }> => {
      return templateClient.createTemplate(input)
    },
    [templateClient]
  )

  const translate = useCallback((key: string, fallback?: string) => t(key, fallback ?? key), [t])

  // Suggestions for the free-form type combobox: seed with a
  // neutral default ('document') plus every type already in use
  // in the org. Labels equal values — no i18n round-trip — so the
  // user sees exactly what gets saved.
  const documentTypes = useMemo(() => {
    const merged = Array.from(new Set(['document', ...knownTypes]))
    return merged.map((value) => ({ value, label: value }))
  }, [knownTypes])

  return (
    <ConsentGuard>
      <TemplateListPage
        key={location.key}
        basePath="/pdf/v2"
        onLoadTemplates={handleLoadTemplates}
        onDeleteTemplate={handleDeleteTemplate}
        onDuplicateTemplate={handleDuplicateTemplate}
        onCreateTemplate={handleCreateTemplate}
        documentTypes={documentTypes}
        allowCustomType
        // V2 list: badges yes, but every row clickable — the new
        // editor opens v1 with a warn-modal and promotes on save.
        showEditorVersionBadge
        t={translate}
      />
    </ConsentGuard>
  )
}

/**
 * Log Audit Page - shows audit logs with filtering and export
 */
export function LogAuditPageWithProviders() {
  const { t, i18n } = useTranslation('logAudit')
  const locale = (i18n.language === 'de' ? 'de' : 'en') as 'de' | 'en'
  return (
    <ConsentGuard>
      <LogAuditPage t={t} locale={locale} />
    </ConsentGuard>
  )
}

// ═══════════════════════════════════════════════════════════════════════════
// Settings Pages
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Settings Account Page - GDPR-compliant account management
 */
export function SettingsAccountPageWithProviders() {
  return (
    <SettingsLayout>
      <AccountSettings />
    </SettingsLayout>
  )
}

export function SettingsSystemPageWithProviders() {
  return <SettingsSystemPage />
}

export function ChangelogPageWithProviders() {
  return <ChangelogPage />
}

export function CookieSettingsPageWithProviders() {
  return <CookieSettingsPage />
}

export function LegalSettingsPageWithProviders() {
  return <LegalSettingsPage />
}
